import { inflateSync } from "fflate";

const END_OF_CENTRAL_DIRECTORY = 0x0605_4b50;
const CENTRAL_DIRECTORY_FILE_HEADER = 0x0201_4b50;
const LOCAL_FILE_HEADER = 0x0403_4b50;
const MAX_ZIP_COMMENT_LENGTH = 65_535;

export interface ZipEntry {
  readonly name: string;
  readonly directory: boolean;
  readonly compressionMethod: number;
  readonly compressedSize: number;
  readonly uncompressedSize: number;
  readonly flags: number;
  readonly localHeaderOffset: number;
}

function assertRange(
  data: Uint8Array,
  offset: number,
  length: number,
  context: string,
): void {
  if (offset < 0 || length < 0 || offset + length > data.length) {
    throw new Error(`Invalid ZIP structure while reading ${context}`);
  }
}

function viewOf(data: Uint8Array): DataView {
  return new DataView(data.buffer, data.byteOffset, data.byteLength);
}

function readUnsignedShort(view: DataView, offset: number): number {
  return view.getUint16(offset, true);
}

function readUnsignedInt(view: DataView, offset: number): number {
  return view.getUint32(offset, true);
}

function decodeName(bytes: Uint8Array): string {
  return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
}

/**
 * Minimal ZIP/JAR/JMOD reader with lazy entry inflation.
 *
 * JMOD files prepend a four-byte header before their ZIP payload. The central
 * directory stores offsets relative to the ZIP payload, so the parser derives
 * and applies the archive prefix instead of assuming offsets start at byte 0.
 */
export class ZipArchive {
  private readonly data: Uint8Array;
  private readonly entriesByName = new Map<string, ZipEntry>();
  private readonly extractedEntries = new Map<string, Uint8Array>();
  private readonly archivePrefix: number;
  private readonly cacheExtractedEntries: boolean;

  public readonly entries: readonly ZipEntry[];

  public constructor(source: Uint8Array, cacheExtractedEntries = true) {
    this.data = source;
    this.cacheExtractedEntries = cacheExtractedEntries;
    const view = viewOf(source);
    const endOffset = this.findEndOfCentralDirectory(view);

    assertRange(source, endOffset, 22, "end-of-central-directory record");

    const diskNumber = readUnsignedShort(view, endOffset + 4);
    const centralDirectoryDisk = readUnsignedShort(view, endOffset + 6);
    const diskEntryCount = readUnsignedShort(view, endOffset + 8);
    const entryCount = readUnsignedShort(view, endOffset + 10);
    const centralDirectorySize = readUnsignedInt(view, endOffset + 12);
    const recordedCentralOffset = readUnsignedInt(view, endOffset + 16);

    if (diskNumber !== 0 || centralDirectoryDisk !== 0) {
      throw new Error("Multi-disk ZIP archives are not supported");
    }

    if (diskEntryCount !== entryCount) {
      throw new Error("Inconsistent ZIP central-directory entry counts");
    }

    if (
      entryCount === 0xffff ||
      centralDirectorySize === 0xffff_ffff ||
      recordedCentralOffset === 0xffff_ffff
    ) {
      throw new Error("ZIP64 archives are not supported");
    }

    const actualCentralOffset = endOffset - centralDirectorySize;
    assertRange(
      source,
      actualCentralOffset,
      centralDirectorySize,
      "central directory",
    );

    if (readUnsignedInt(view, actualCentralOffset) !== CENTRAL_DIRECTORY_FILE_HEADER) {
      throw new Error("ZIP central directory could not be located");
    }

    this.archivePrefix = actualCentralOffset - recordedCentralOffset;

    const entries: ZipEntry[] = [];
    let offset = actualCentralOffset;

    for (let index = 0; index < entryCount; index++) {
      assertRange(source, offset, 46, "central-directory file header");

      if (readUnsignedInt(view, offset) !== CENTRAL_DIRECTORY_FILE_HEADER) {
        throw new Error(`Invalid ZIP central-directory entry at index ${index}`);
      }

      const flags = readUnsignedShort(view, offset + 8);
      const compressionMethod = readUnsignedShort(view, offset + 10);
      const compressedSize = readUnsignedInt(view, offset + 20);
      const uncompressedSize = readUnsignedInt(view, offset + 24);
      const fileNameLength = readUnsignedShort(view, offset + 28);
      const extraLength = readUnsignedShort(view, offset + 30);
      const commentLength = readUnsignedShort(view, offset + 32);
      const recordedLocalHeaderOffset = readUnsignedInt(view, offset + 42);
      const recordLength = 46 + fileNameLength + extraLength + commentLength;

      assertRange(source, offset, recordLength, "central-directory entry");

      if (
        compressedSize === 0xffff_ffff ||
        uncompressedSize === 0xffff_ffff ||
        recordedLocalHeaderOffset === 0xffff_ffff
      ) {
        throw new Error("ZIP64 entries are not supported");
      }

      const nameStart = offset + 46;
      const name = decodeName(source.subarray(nameStart, nameStart + fileNameLength));
      const entry: ZipEntry = {
        name,
        directory: name.endsWith("/"),
        compressionMethod,
        compressedSize,
        uncompressedSize,
        flags,
        localHeaderOffset: recordedLocalHeaderOffset + this.archivePrefix,
      };

      entries.push(entry);
      this.entriesByName.set(name, entry);
      offset += recordLength;
    }

    this.entries = entries;
  }

  public has(name: string): boolean {
    return this.entriesByName.has(name);
  }

  public getEntry(name: string): ZipEntry | null {
    return this.entriesByName.get(name) ?? null;
  }

  public extract(name: string): Uint8Array {
    if (this.cacheExtractedEntries) {
      const cached = this.extractedEntries.get(name);
      if (cached !== undefined) {
        return cached;
      }
    }

    const entry = this.entriesByName.get(name);
    if (entry === undefined) {
      throw new Error(`ZIP entry not found: ${name}`);
    }

    if (entry.directory) {
      const empty = new Uint8Array();
      if (this.cacheExtractedEntries) {
        this.extractedEntries.set(name, empty);
      }
      return empty;
    }

    if ((entry.flags & 1) !== 0) {
      throw new Error(`Encrypted ZIP entry is not supported: ${name}`);
    }

    const view = viewOf(this.data);
    const offset = entry.localHeaderOffset;
    assertRange(this.data, offset, 30, `local header for ${name}`);

    if (readUnsignedInt(view, offset) !== LOCAL_FILE_HEADER) {
      throw new Error(`Invalid ZIP local header: ${name}`);
    }

    const fileNameLength = readUnsignedShort(view, offset + 26);
    const extraLength = readUnsignedShort(view, offset + 28);
    const dataOffset = offset + 30 + fileNameLength + extraLength;
    assertRange(this.data, dataOffset, entry.compressedSize, `data for ${name}`);

    const compressed = this.data.subarray(
      dataOffset,
      dataOffset + entry.compressedSize,
    );
    let extracted: Uint8Array;

    switch (entry.compressionMethod) {
      case 0:
        extracted = Uint8Array.from(compressed);
        break;
      case 8:
        extracted = inflateSync(compressed);
        break;
      default:
        throw new Error(
          `Unsupported ZIP compression method ${entry.compressionMethod}: ${name}`,
        );
    }

    if (extracted.length !== entry.uncompressedSize) {
      throw new Error(
        `Unexpected uncompressed size for ${name}: expected ${entry.uncompressedSize}, received ${extracted.length}`,
      );
    }

    if (this.cacheExtractedEntries) {
      this.extractedEntries.set(name, extracted);
    }
    return extracted;
  }

  private findEndOfCentralDirectory(view: DataView): number {
    const minimumOffset = Math.max(
      0,
      this.data.length - 22 - MAX_ZIP_COMMENT_LENGTH,
    );

    for (let offset = this.data.length - 22; offset >= minimumOffset; offset--) {
      if (readUnsignedInt(view, offset) !== END_OF_CENTRAL_DIRECTORY) {
        continue;
      }

      const commentLength = readUnsignedShort(view, offset + 20);
      if (offset + 22 + commentLength === this.data.length) {
        return offset;
      }
    }

    throw new Error("ZIP end-of-central-directory record not found");
  }
}
